import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getCases, 
  likeCase, 
  getCase, 
  addClinicalInput, 
  createCase, 
  updateCase,
  saveCase, 
  deleteCase,
  getSavedCases, 
  getMyCases, 
  getMyInputs, 
  getStudentStats,
  getClinicalInputs, 
  editClinicalInput, 
  deleteClinicalInput,
  likeClinicalInput,
  requestCaseAccess,
  getCaseAccessRequests,
  updateAccessRequestStatus,
  getMyNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from '../api/cases-api';
import { uploadCaseMedia, deleteCaseMedia } from '../api/media-api';

export const useUploadCaseMedia = () => {
  return useMutation({
    mutationFn: ({ file, section }) => uploadCaseMedia(file, section),
  });
};

export const useDeleteCaseMedia = () => {
  return useMutation({
    mutationFn: (mediaId) => deleteCaseMedia(mediaId),
  });
};

export const useCases = (page = 1, limit = 20, filters = {}) => {
  return useQuery({
    queryKey: ['cases', page, limit, filters],
    queryFn: () => getCases({ page, limit, ...filters }),
  });
};

export const useCase = (caseId) => {
  return useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: !!caseId && caseId !== 'case-1' && caseId !== 'case-2',
  });
};

export const useCaseInputs = (caseId) => {
  return useQuery({
    queryKey: ['caseInputs', caseId],
    queryFn: () => getClinicalInputs(caseId),
    enabled: !!caseId && caseId !== 'case-1' && caseId !== 'case-2',
  });
};

export const useLikeCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseId) => likeCase(caseId),
    onSuccess: (_, caseId) => {
      // Invalidate and refetch cases query to get updated likes
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', caseId] });
    },
  });
};

export const useAddClinicalInput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addClinicalInput,
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: ['case', caseId] });
      queryClient.invalidateQueries({ queryKey: ['caseInputs', caseId] });
    },
  });
};

export const useEditClinicalInput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editClinicalInput,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseInputs'] });
      queryClient.invalidateQueries({ queryKey: ['case'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
};

export const useDeleteClinicalInput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputId) => deleteClinicalInput(inputId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseInputs'] });
      queryClient.invalidateQueries({ queryKey: ['case'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['myInputs'] });
    },
  });
};

export const useLikeClinicalInput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputId) => likeClinicalInput(inputId),
    onSuccess: (_, inputId) => {
      queryClient.invalidateQueries({ queryKey: ['caseInputs'] });
    },
  });
};

export const useCreateCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCase,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
};

export const useUpdateCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCase,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['myCases'] });
      if (variables?.caseId) {
        queryClient.invalidateQueries({ queryKey: ['case', variables.caseId] });
      }
    },
  });
};

export const useDeleteCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseId) => deleteCase(caseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['myCases'] });
    },
  });
};

export const useSaveCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseId) => saveCase(caseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedCases'] });
    },
  });
};

export const useSavedCases = (page = 1, limit = 20, q) => {
  return useQuery({
    queryKey: ['savedCases', page, limit, q],
    queryFn: () => getSavedCases({ page, limit, q }),
  });
};

export const useMyCases = (page = 1, limit = 20, q) => {
  return useQuery({
    queryKey: ['myCases', page, limit, q],
    queryFn: () => getMyCases({ page, limit, q }),
  });
};

export const useMyInputs = (page = 1, limit = 20, q) => {
  return useQuery({
    queryKey: ['myInputs', page, limit, q],
    queryFn: () => getMyInputs({ page, limit, q }),
  });
};

export const useStudentStats = () => {
  return useQuery({
    queryKey: ['studentStats'],
    queryFn: getStudentStats,
  });
};

// Access Request Hooks
export const useRequestAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requestCaseAccess,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', variables.caseId] });
    },
  });
};

export const useCaseAccessRequests = (caseId) => {
  return useQuery({
    queryKey: ['access-requests', caseId],
    queryFn: () => getCaseAccessRequests(caseId),
    enabled: !!caseId,
  });
};

export const useUpdateAccessRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAccessRequestStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
    },
  });
};

// Notification Hooks
export const useMyNotifications = () => {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: getMyNotifications,
  });
};

export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadNotificationCount,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};